import { useState } from 'react'
import axios from 'axios'
import Cookie from 'js-cookie'
import { useQueryClient } from 'react-query'
import { useNavigate } from 'react-router-dom'
import toastService from '@/components/toast/toast-service'
import Constants from '@/configs/constants'
import { vrAccessPermissionCodeMap } from '@/configs/accessControl'
import { endPoints } from '@/configs/endPoints'
import { routeNames } from '@/configs/routePaths'
import useLocalStorage from '@/hooks/common/use-local-storage'
import useSessionStorage from '@/hooks/common/use-session-storage'
import { IPermissoes } from '@/interfaces/user'
import { getBranchUserAccount } from '@/services/branches'
import { getUserPermissions, getMigrationStatus } from '@/services/users'
import { createContext } from '@/utils/create-context'
import { API_URL } from '@/utils/fetcher'
import { TokenStorage } from '@/utils/token-storage'
import { authorizeVrAccess } from '@/services/authentication'
import { IAuthorizeVrAccessRequest } from '@/interfaces/vrAccess'
 
interface IContextProps {
  logOut: () => void
  isAuthenticated: boolean
  isAllowedV2Pedidos: boolean
  legacyAuth: (token: string) => void
  vrAccessAuth: (authorizeVrAccessRequest: IAuthorizeVrAccessRequest, targetRoute: string) => Promise<void>
  isLoadingAuth: boolean
  isLoadingRedirect: boolean
  handleSelectAccount: (selectedBranch: string) => void
  handleRedirectDashboard: (token: string) => void
  userPermissions: IPermissoes[]
  setStoragePermissions: (permissions: IPermissoes[]) => void
  empregadorId: string | null
  allowAdditionalOrderTypes: boolean,
  billingClosingDay: number | null,
  unifiedBilling: boolean | null,
  allowMiniSiteReports: boolean,
  allowNewImport: boolean
}

interface IProviderProps {
  children: JSX.Element
}

const [useUserContext, UserContext] = createContext<IContextProps>()

export const buildPermissionsFromVrAccessCodes = (codes: string[], idEmpregador: string): IPermissoes[] => {
  return codes
    .filter(code => vrAccessPermissionCodeMap[code] !== undefined)
    .map(code => ({
      accessControlId: '',
      system: 'ABSCardVR',
      functionName: code,
      moduleName: '',
      sort: '',
      code: vrAccessPermissionCodeMap[code],
      affiliateNative: null,
      clientNative: null,
      idEmpregador,
    }))
}

const UserProvider = ({ children }: IProviderProps) => {
  const isAuthenticated = Boolean(Cookie.get(Constants.TOKEN_STORAGE_KEY))
  const [storagePermissions, setStoragePermissions] = useLocalStorage(
    'userPermissions',
    ''
  )

  const AppUrl = process.env.VITE_APP_ONIX_URL ?? ''
  const queryClient = useQueryClient()
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(false)
  const [isAuth, setIsAuth] = useState(false)
  const [isLogOut, setIsLogOut] = useState(false)
  const [token, setToken] = useState('')
  const [isLoadingRedirect, setIsLoadingRedirect] = useState<boolean>(false)
  const [isAllowedV2Pedidos, setIsAllowedV2Pedidos] = useLocalStorage('isAllowedV2Pedidos', '')
  const [allowMiniSiteReports, setAllowMiniSiteReports] = useLocalStorage('allowMiniSiteReports', false);
  const [allowNewImport, setAllowNewImport] = useLocalStorage('allowNewImport', false);
  const [userPermissions, setUserPermissions] = useState<IPermissoes[]>(
    storagePermissions ? (storagePermissions as IPermissoes[]) : []
  )
  const [empregadorId, setEmpregadorId] = useSessionStorage<string | null>('empregadorId', null)
  const [allowAdditionalOrderTypes, setAllowAdditionalOrderTypes] = useSessionStorage<boolean>('allowAdditionalOrderTypes', false)
  const [_, setIsVrAccess] = useSessionStorage(Constants.VRACCESS_SESSION_KEY, false);
  const navigate = useNavigate()
  const [_speMigrationJourney, setSpeMigrationJourney] = useLocalStorage(Constants.SPE_MIGRATION_JOURNEY_DATA, null);
  const checkStatusMigration = false;
  const [billingClosingDay, setBillingClosingDay] = useSessionStorage<number>('billingClosingDay', null)
  const [unifiedBilling, setUnifiedBilling] = useSessionStorage<boolean>('unifiedBilling', false)

  const logOut = async () => {
    setIsLogOut(true)
    setIsAuth(true)

    setTimeout(() => {
      setIsAuth(false)
      setIsLogOut(false)
      window.location.href = routeNames.login
    }, 1000)

    const speDisabledModal = localStorage.getItem('speDisabledModal'); 
    localStorage.clear();
    localStorage.setItem('speDisabledModal', speDisabledModal);
    TokenStorage.logout();
    queryClient.clear();
    sessionStorage.clear();
  }

  const legacyAuth = async (token: string | null) => {
    if (token) {
      setIsLoadingAuth(true)
      await axios
        .get(API_URL + endPoints.accounts, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        .then(() => {
          toastService.success('Login realizado!')
          TokenStorage.storeToken(token)
          navigate(routeNames.selectAccounts)
        })
        .catch(error => {
          const messsage =
            error?.response?.data?.userMessage ?? Constants.ERROR_DATA
          toastService.error(messsage)
        })
        .finally(() => {
          setIsLoadingAuth(false)
        })
    }
  }

  const handleSetUserLoginData = (data: any) => {
    setIsAllowedV2Pedidos(data.allowedToPedidosNewVersion)
    setAllowAdditionalOrderTypes(data.allowAdditionalOrderTypes ?? false)
    setBillingClosingDay(data?.billingClosingDay ?? null)
    setUnifiedBilling(data.unifiedBilling ?? false) 
    setAllowMiniSiteReports(data.allowMiniSiteReports ?? false);
    setAllowNewImport(data.allowNewImport ?? false);
  }

  const vrAccessAuth = async (authorizeVrAccessRequest: IAuthorizeVrAccessRequest, targetRoute: string) : Promise<void> => {
    await authorizeVrAccess(authorizeVrAccessRequest)
      .then(data => {
        setEmpregadorId(data.accountId)
        TokenStorage.storeToken(data.token?.token);
        handleSetUserLoginData(data);
        return data;
      })
      .then(content => {
        const doRedirect = () => {
          if (content.token.token) {
            TokenStorage.storeToken(content.token.token);
            setToken(content.token.token);
            setIsAuth(true);
            setIsVrAccess(authorizeVrAccessRequest.isVrAccessDeepLink);
            setTimeout(() => {
              if (allowMiniSiteReports && targetRoute === `/${routeNames.relatoriosOld}`) {
                targetRoute = `/${routeNames.relatorios}`;
              }
              if (!allowNewImport && targetRoute === `/${routeNames.importacaoOld}`) {
                targetRoute = `/${routeNames.importacao}`;
              }
              window.location.href = targetRoute;
            }, 3000);
          } else {
            TokenStorage.logout()
          }
        }

        if (content.permissions && content.permissions.length > 0) {
          const permissions = buildPermissionsFromVrAccessCodes(content.permissions, content.accountId)
          setUserPermissions(permissions)
          setStoragePermissions(permissions)
          doRedirect()
        } else {
          getUserPermissions({
            idEmpregador: content.accountId ?? '',
            sistema: Constants.APP_SISTEM ?? ''
          })
            .then(data => {
              setUserPermissions(data.conteudo.controlesDeAcesso)
              setStoragePermissions(data.conteudo.controlesDeAcesso)
            })
            .then(() => doRedirect())
        }
      })
  }

  const handleMigrationStatus = async () => {
    try {
      await getMigrationStatus()
        .then((content) => {
          setSpeMigrationJourney(content ?? null);      
        });
    } catch {
      setSpeMigrationJourney(null);
    }
  }

  const handleSelectAccount = async (selectedBranch: string, route: string = null) => {
    setIsLoadingRedirect(true);
    if(checkStatusMigration){
      handleMigrationStatus();
    }
    try {
      await getBranchUserAccount(selectedBranch ?? '')
        .then(data => {
          setEmpregadorId(data.authenticatedAccountId)
          handleSetUserLoginData(data);
          return data
        })
        .then(content => {
          getUserPermissions({
            idEmpregador: content.authenticatedAccountId ?? '',
            sistema: Constants.APP_SISTEM ?? ''
          })
            .then(data => {
              setUserPermissions(data.conteudo.controlesDeAcesso)
              setStoragePermissions(data.conteudo.controlesDeAcesso)
            })
            .then(() => {
              if (content.token.token) {
                TokenStorage.storeToken(content.token.token);
                setToken(content.token.token);
                setIsAuth(true);
                setIsLoadingRedirect(false);
                if(route)
                  navigate(route);
                else
                  navigate(routeNames.sanitization);
              } else {
                TokenStorage.logout()
              }
            })
            .finally(() => {
              setIsLoadingRedirect(false)
            })
        })
    } catch (error) {
      setIsLoadingRedirect(false)
    }
  }

  const handleRedirectDashboard = (token: string) => {
    if (token) {
      setIsLoadingAuth(true)
      setToken(token)
      setIsAuth(true)
      TokenStorage.storeToken(token)
      setTimeout(() => {
        toastService.success('Login realizado!')
        navigate(routeNames.dashboard)
        setIsLoadingAuth(false)
      }, 1500)
    }
  }

  return (
    <>
      <UserContext
        value={{
          isLoadingRedirect,
          logOut,
          isAuthenticated,
          isAllowedV2Pedidos,
          legacyAuth,
          vrAccessAuth,
          isLoadingAuth,
          handleSelectAccount,
          handleRedirectDashboard,
          setStoragePermissions,
          userPermissions,
          empregadorId,
          allowAdditionalOrderTypes,
          billingClosingDay,
          unifiedBilling,
          allowMiniSiteReports,
          allowNewImport
        }}
      >
        {children}
      </UserContext>
      {isAuth && !isLogOut && (
        <iframe
          title='auth-iframe'
          width={50}
          height={50}
          className='absolute top-0 opacity-0 pointer-events-none overflow-hidden z-[-1]'
          src={AppUrl + `${endPoints.authFromMicrosservice}?token=` + token}
        />
      )}
      {isAuth && isLogOut && (
        <iframe
          title='logout-iframe'
          width={50}
          height={50}
          className='absolute top-0 opacity-0 pointer-events-none overflow-hidden z-[-1]'
          src={AppUrl + endPoints.logoutExternal}
        />
      )}
    </>
  )
}

export { UserProvider, useUserContext }
